import React, { useEffect, useMemo, useState } from "react";
import { cleanedData, rerunCIPs } from "../../network/cipAPI";
import Charts from "../../FileUpload/chart";
import HistoricalCIPS from "../historicalCIPS";
import { connect, useDispatch, useSelector } from "react-redux";
import PageLoading from "../../components/PageLoading";
import { storeCIPS } from "../../Store/TemplateStore/template.action";
import CriticalDeleteDialog from "../../components/CriticalDeleteDialog";
import moment from "moment";
import { useTranslation } from "react-i18next";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { SettingsIcon } from "../../assets/icons";
import AdvancedSettings from "../analyseData/AdvancedSettings/AdvancedSettings";

const IdentifyCIP = ({
  cips,
  duplicateCip,
  setDuplicateCip,
  activeStep,
  setSettingsButton,
}) => {
  const { t } = useTranslation();
  const [date, setPlotDate] = useState(useSelector((state) => state.cleanDate));
  const [cleaningResponse, setCleaningResponse] = useState(
    useSelector((state) => state.cleanData),
  );
  const selectedTrain = useSelector((state) => state.selTrain);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateCIPStartDate, setDuplicateCIPStartDate] = useState([]);
  const dispatch = useDispatch();

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const handleSettingsDialogOpen = () => {
    setIsSettingsDialogOpen(true);
  };

  const handleSettingsDialogClose = () => {
    setIsSettingsDialogOpen(false);
  };

  const handleSave = () => {
    getCleanedData(true);
  };

  useEffect(() => {
    setSettingsButton?.(
      <WPTertiaryButton
        onClick={handleSettingsDialogOpen}
        disabled={isLoading}
        label={t("analyseData.settings")}
        icon={<SettingsIcon />}
        iconPlacement="before"
        size="medium"
        variant="outlined"
      />,
    );
    return () => setSettingsButton?.(null);
  }, [isLoading]);

  useEffect(() => {
    if (duplicateCip) {
      const startDate = Object.values(duplicateCip).map((item) => {
        return duplicateCip ? moment(item).format("DD/MM/YYYY") : "";
      });
      setDuplicateCIPStartDate(startDate);
    }
  }, [duplicateCip]);

  const getCleanedData = async (shouldRerun = false) => {
    try {
      setIsLoading(true);

      // If shouldRerun is true, call the rerun API first
      if (shouldRerun) {
        try {
          const response = await rerunCIPs(selectedTrain);

          if (response && response.status === 422) {
            const errorMsg =
              response.data?.detail || t("notificationStrings.validationError");
            createNotification(ERROR_NOTIFICATION, errorMsg);
            setIsLoading(false);
            return;
          }
        } catch (rerunError) {
          if (rerunError.response) {
            const errorMsg =
              rerunError.message || t("notificationStrings.somethingWrong");
            createNotification(ERROR_NOTIFICATION, errorMsg);
          }

          setIsLoading(false);
          return;
        }
      }

      const response = await cleanedData(selectedTrain);
      const data = JSON.parse(response?.data?.cleaned_data);
      setIsLoading(false);
      const date = data.date;
      setPlotDate(date);
      setCleaningResponse(data);
      dispatch({
        type: "STORE_CLEAN_DATA",
        payload: {
          cleanData: data,
          cleanDate: date,
        },
      });
    } catch (err) {
      const errorMsg = err.message || t("notificationStrings.somethingWrong");
      createNotification(ERROR_NOTIFICATION, errorMsg);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (!cleaningResponse) {
      getCleanedData();
    }
  }, []);

  const onBackClicked = () => {
    setDuplicateCip(false);
  };

  return (
    <div>
      {isLoading ? (
        <PageLoading customHeight={"350px"} />
      ) : (
        <div>
          {cleaningResponse && duplicateCip?.length > 0 && (
            <CriticalDeleteDialog
              type="error"
              title={t("IdentifiedCIPStrings.duplicateCIP")}
              description={t("IdentifiedCIPStrings.duplicateCIPBody").replace(
                "<date>",
                duplicateCIPStartDate.join(", "),
              )}
              onDialogClose={onBackClicked}
              cancelBtnText={t("ValueStrings.back")}
            />
          )}
          <div>
            <div className="descriptionText marginTop10 marginBtm10">
              {t("IdentifiedCIPStrings.chartDescription")}
            </div>
            <Charts
              cleaningResponse={cleaningResponse}
              date={date}
              cips={cips}
              activeStep={activeStep}
            />
            <HistoricalCIPS />
          </div>
          <div className="ro-disclaimer">
            {t("disclaimer", { currentYear })}
          </div>
        </div>
      )}

      {isSettingsDialogOpen && (
        <AdvancedSettings
          handleClose={handleSettingsDialogClose}
          selectedTrain={selectedTrain}
          cipAndShutdownIdentification
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  cips: state.cips,
});

const mapDispatchToProps = {
  storeCIPS,
};

export default connect(mapStateToProps, mapDispatchToProps)(IdentifyCIP);
